import { TabPanel, TabView } from 'primereact/tabview';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Navigate, useLocation, useParams } from 'react-router-dom';

import ActionButtons from '@features/campaigns/details/components/ActionButtons/ActionButtons';
import CampaignDetails from '@features/campaigns/details/components/CampaignDetails/CampaignDetails';
import Performance from '@features/campaigns/performance/containers/Performance/Performance';
import { CombinedSequentialId } from '@features/campaigns/shared/components/CombinedSequentialId/CombinedSequentialId.tsx';
import { Spinner } from '@features/campaigns/shared/components/Spinner/Spinner';
import useCampaignDetails from '@features/campaigns/shared/hooks/useCampaignDetails';
import { CampaignTypes } from '@features/campaigns/shared/types/campaign.ts';
import { AccessRights } from '@shared/api/commonApiInterfaces.ts';
import { InfoPanel } from '@shared/components/InfoPanel/InfoPanel.tsx';
import { InfoPanelTypes, InfoPanelVariants } from '@shared/components/InfoPanel/InfoPanel.types.ts';
import { LinkText } from '@shared/components/LinkText/LinkText.tsx';
import placeholderCommonStyles from '@shared/components/PlaceholderCard/PlaceholderCard.module.scss';
import { PlaceholderCard } from '@shared/components/PlaceholderCard/PlaceholderCard.tsx';
import { i18nNameSpace } from '@shared/consts/i18n';
import { useUserProfileData } from '@shared/hooks/useUserProfileData.ts';
import { FeatureFlagPossibleState } from '@shared/providers/FeatureFlagsProvider/providerSetup.ts';
import { useFeatureFlags } from '@shared/providers/FeatureFlagsProvider/useFeatureFlags.tsx';
import { useUserDetails } from '@shared/providers/UserDetailsProvider/useUserDetails.tsx';
import { routePaths } from '@shared/router/routePaths';
import { ONBOARDING_STATE, PossibleOnboardingState } from '@shared/types/userProfile.types.ts';

import { campaignDetailsDTO, CampaignDetailsViewData } from './CampaignDetailsPage.dto';
import styles from './CampaignDetailsPage.module.scss';

function determineInfoPanelMessage(onboardingState?: PossibleOnboardingState) {
  switch (onboardingState) {
    case ONBOARDING_STATE.BILLING_INFO_NOT_FILLED:
      return {
        message: 'builder.steps.step05.infoPanels.provideBillingInfo',
        type: InfoPanelTypes.WARNING,
      };
    case ONBOARDING_STATE.BILLING_INFO_IN_APPROVAL:
      return {
        message: 'builder.steps.step05.infoPanels.billingInfoInApproval',
        type: InfoPanelTypes.INFO,
      };
    case ONBOARDING_STATE.ADVERTISERS_NOT_ADDED:
      return {
        message: 'builder.steps.step05.infoPanels.advertisersNotAdded',
        type: InfoPanelTypes.WARNING,
      };
    case ONBOARDING_STATE.ADVERTISERS_IN_APPROVAL:
      return {
        message: 'builder.steps.step05.infoPanels.advertisersInApproval',
        type: InfoPanelTypes.INFO,
      };
    default:
      return {
        message: '',
        type: InfoPanelTypes.INFO,
      };
  }
}

function CampaignDetailsPage() {
  const { t } = useTranslation(i18nNameSpace.CAMPAIGNS);
  const { FeatureFlag_Step01_External } = useFeatureFlags();
  const { pathname } = useLocation();
  const { campaignId } = useParams();
  const isPerformanceView = pathname.includes(routePaths.campaignPerformance.url(campaignId!));
  const activeTabIndex = isPerformanceView ? '1' : '0';
  const [activeIndex, setActiveIndex] = useState(activeTabIndex);

  const { isFetching, data } = useCampaignDetails({ campaignId: campaignId! });
  const setActiveTabHandler = (tabIndex: number) => {
    setActiveIndex(`${tabIndex}`);
  };

  const { isExternalAgency, isExternalDirectClient } = useUserDetails();
  const isCurrentUserExternal = isExternalDirectClient || isExternalAgency;
  const isSDEFeatureFlagActive = FeatureFlag_Step01_External === FeatureFlagPossibleState.ACTIVE;
  const { onboardingState } = useUserProfileData({
    canFetchUserProfileData: isCurrentUserExternal && isSDEFeatureFlagActive,
  });

  if (!data) {
    return (
      <div className={placeholderCommonStyles.placeholderCardWrapper}>
        <PlaceholderCard textKey='placeholderLoading' />
      </div>
    );
  }

  const campaignDetailsData: CampaignDetailsViewData = campaignDetailsDTO(data);
  const invalidSections = data.invalidSections;
  const isCampaignSelfService = campaignDetailsData.type === CampaignTypes.SELF_SERVICE;
  const areAllRequiredFieldFilledIn = invalidSections.length === 0;
  const areRequiredCreativesUploadedForExternalCampaign = isCampaignSelfService
    ? Boolean(campaignDetailsData.adProducts.landingImageUrl) && Boolean(campaignDetailsData.adProducts.lShapeImageUrl)
    : false;
  const isAdvertiserAndBillingContactNotAddedForExternalCampaign =
    isSDEFeatureFlagActive &&
    isCampaignSelfService &&
    !campaignDetailsData.campaignDetails.advertiserCode &&
    !campaignDetailsData.campaignDetails.billingContactCode;

  const shouldDisplayPerformanceTab = campaignDetailsData.accessRights.includes(AccessRights.EXPORT_REPORT);
  const areAllConditionsMetForExternalCampaign = isCampaignSelfService
    ? !areRequiredCreativesUploadedForExternalCampaign || isAdvertiserAndBillingContactNotAddedForExternalCampaign
    : false;

  const shouldDisableSubmitButton = !areAllRequiredFieldFilledIn || areAllConditionsMetForExternalCampaign;

  const shouldShowInfoPanel =
    onboardingState === ONBOARDING_STATE.BILLING_INFO_NOT_FILLED ||
    onboardingState === ONBOARDING_STATE.BILLING_INFO_IN_APPROVAL ||
    onboardingState === ONBOARDING_STATE.ADVERTISERS_NOT_ADDED ||
    onboardingState === ONBOARDING_STATE.ADVERTISERS_IN_APPROVAL;

  const infoPanelData = determineInfoPanelMessage(onboardingState);

  const advertiserBillingContactNotFilled =
    (onboardingState === ONBOARDING_STATE.COMPLETED &&
      !(
        campaignDetailsData.campaignDetails.advertiserName && campaignDetailsData.campaignDetails.billingContactName
      )) ||
    false;

  return (
    <div className={styles.wrapper}>
      {!isPerformanceView && isFetching && <Spinner variant='fullScreenOverlay' />}
      <div className={styles.contentWrapper}>
        <ActionButtons
          shouldSubmitButtonBeDisabled={shouldDisableSubmitButton}
          campaignName={campaignDetailsData.campaignDetails.campaignName}
          campaignAccessRights={campaignDetailsData.accessRights}
        />
        <div className={styles.campaignDetailsWrapper}>
          <h2 className={styles.campaignName}>{campaignDetailsData.campaignDetails.campaignName}</h2>
          <p className={styles.campaignSubname}>
            <CombinedSequentialId
              sequentialId={campaignDetailsData.sequentialId}
              billingSequentialId={campaignDetailsData.billingSequentialId}
              type={campaignDetailsData.type}
            />
          </p>
        </div>
        <TabView activeIndex={Number(activeIndex)} onTabChange={(e) => setActiveTabHandler(e.index)}>
          <TabPanel header={t('page.campaignDetails.viewTab.campaignDetails')}>
            {campaignId && <Navigate to={routePaths.campaignDetails.url(campaignId)} />}

            {shouldShowInfoPanel && (
              <InfoPanel type={infoPanelData.type} variant={InfoPanelVariants.COMPACT}>
                <p>
                  <Trans
                    t={t}
                    i18nKey={infoPanelData.message}
                    components={[
                      <LinkText
                        externalLink={false}
                        key={'infoPanels.step05InfoPanel'}
                        target='_self'
                        to={routePaths.userAdminPanel.routePath}
                      />,
                    ]}
                  />
                </p>
              </InfoPanel>
            )}

            {advertiserBillingContactNotFilled && (
              <InfoPanel type={InfoPanelTypes.WARNING} variant={InfoPanelVariants.COMPACT}>
                <p>
                  <Trans
                    t={t}
                    i18nKey={'builder.steps.step05.infoPanels.advertisersReadyNotAdded'}
                    components={[
                      <LinkText
                        externalLink={false}
                        key={'infoPanels.advertisersReadyNotAdded'}
                        target='_self'
                        to={routePaths.campaignBuilderStep01.url(campaignId)}
                      />,
                    ]}
                  />
                </p>
              </InfoPanel>
            )}

            <CampaignDetails
              data={campaignDetailsData}
              invalidSections={invalidSections}
              onboardingState={onboardingState}
              isCurrentUserExternal={isCurrentUserExternal}
            />
          </TabPanel>
          {shouldDisplayPerformanceTab && (
            <TabPanel header={t('page.campaignDetails.viewTab.performance')}>
              {campaignId && <Navigate to={routePaths.campaignPerformance.url(campaignId)} />}
              <Performance
                campaignEndDate={campaignDetailsData.endDate}
                campaignStartDate={campaignDetailsData.startDate}
                campaignFormat={campaignDetailsData.formatType}
              />
            </TabPanel>
          )}
        </TabView>
      </div>
    </div>
  );
}

export default CampaignDetailsPage;
